'use client';

import { useEffect, useRef, useState, type RefObject } from 'react';
import type { XYChartViewportSize } from './xychart-component-model';

const RESIZE_EPSILON = 0.5;

export interface UseXYChartViewportSizeResult {
  size: XYChartViewportSize;
  ref: RefObject<HTMLDivElement | null>;
}

export function useXYChartViewportSize(
  initialSize: XYChartViewportSize
): UseXYChartViewportSizeResult {
  const [size, setSize] = useState<XYChartViewportSize>(initialSize);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;

      const { width, height } = entry.contentRect;
      setSize((previousSize) => {
        const widthChanged = Math.abs(previousSize.width - width) >= RESIZE_EPSILON;
        const heightChanged =
          Math.abs(previousSize.height - height) >= RESIZE_EPSILON;

        if (!widthChanged && !heightChanged) {
          return previousSize;
        }

        return { width, height };
      });
    });

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return { size, ref };
}
